import { useRef, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi";
import { useIntersectionObserver } from "usehooks-ts";
import { debounce } from "lodash";
import Spinner from "../../spinner";

const AsyncSelect = ({
  label,
  options = [],
  hasMore,
  fetchNext,
  defaultLabel = "",
  isLoading,
  value,
  onChange,
}) => {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(true);

  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        if (!isLoading) {
          fetchNext?.(query);
        }
      }
    },
  });

  const onFocus = () => {
    setIsFocused(true);
  };

  const onFocusOut = () => {
    if (value === "" && value === undefined && value === null) {
      setIsFocused(false);
    }
  };

  const debouncedOnSearch = useRef(
    debounce((query) => {
      onSearch(query);
    }, 1000)
  ).current;

  const onSearch = (query) => {
    setQuery(query);
    fetchNext?.(query, true);
  };

  return (
    <div>
      <Combobox value={value} onChange={onChange}>
        <div className="relative">
          <label
            className={
              "absolute transition-all  pointer-events-none px-3" +
              (isFocused ? " text-[0.7rem] pb-5 pt-1 " : " text-base py-3 ")
            }
          >
            {label}
          </label>
          <ComboboxInput
            aria-label=""
            className={
              "w-full rounded-md bg-white pb-2 pt-2 pr-8 pl-3 border border-neutral-400"
            }
            displayValue={(value) => {
              const option = options?.find((el) => el.value === value);
              return option?.label ? option?.label : defaultLabel;
            }}
            onChange={(event) => {
              debouncedOnSearch(event.target.value);
            }}
            onFocus={onFocus}
            onBlur={onFocusOut}
          />
          <ComboboxButton className="group absolute inset-y-0 right-0 mr-3 px-2">
            <HiChevronDown
              className="w-5 h-5 text-neutral group-data-[open]:rotate-180 transition-transform"
              aria-hidden="true"
            />
          </ComboboxButton>
        </div>

        <ComboboxOptions
          anchor="bottom"
          className="rounded-md border empty:invisible w-[var(--input-width)] async-select-max-height !max-h-[20rem] p-2 bg-white z-[2000]"
        >
          {options.map((opt) => (
            <ComboboxOption
              key={opt.value}
              value={opt?.value}
              className="data-[focus]:bg-blue-100 py-1"
            >
              {opt?.label}
            </ComboboxOption>
          ))}
          {options.length === 0 && !isLoading && (
            <p className="text-neutral text-center w-full py-5">
              No options found
            </p>
          )}
          {isLoading && (
            <div className="grid place-items-center">
              <Spinner className={"w-7 h-7"} />
            </div>
          )}
          {hasMore && !isLoading && (
            <div ref={ref} className="w-full py-5"></div>
          )}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
};

export default AsyncSelect;
